import type { GameEndReason } from '@chess-uno/shared-protocol'
import type { Messages } from './fr.js'

/** Plural of a counted word, German rule (only 1 is singular); German plurals are irregular, so both forms are given. */
function n(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`
}

export const de: Messages = {
  common: {
    back: 'Zurück',
    home: 'Startseite',
    cancel: 'Abbrechen',
    loading: 'Wird geladen…',
    error: 'Fehler',
    yes: 'Ja',
    no: 'Nein',
    armies: { w: 'Weiß', b: 'Schwarz' },
    tiers: { bronze: 'Bronze', silver: 'Silber', gold: 'Gold', platinum: 'Platin', diamond: 'Diamant' },
  },

  modes: {
    purist: {
      name: 'Purist',
      tagline: 'Schachmatt',
      desc: 'Wie im Schach: Kein König bleibt im Schach stehen, Könige werden nie geschlagen, gewonnen wird durch Schachmatt.',
      note: 'Purist-Modus: Du darfst deinen König nicht im Schach lassen; gewonnen wird durch Schachmatt.',
    },
    capture: {
      name: 'Königsjagd',
      tagline: 'König schlagen',
      desc: 'Die ursprünglichen Regeln: Ein König darf angegriffen bleiben, und wer ihn schlägt, gewinnt.',
      note: 'Königsjagd-Modus: Ein König darf angegriffen bleiben; wer ihn schlägt, gewinnt.',
    },
  },

  campaign: {
    eyebrow: 'November-Challenge',
    title: 'Beat the machine',
    text: 'Besiege den Computer auf Stufe Mittel und teile deinen Sieg auf Instagram: Jeden Montag wird ein 20-€-Gutschein verlost.',
    link: 'Alle Details auf Instagram',
    dismiss: 'Ausblenden',
  },

  language: {
    names: { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', hi: 'हिन्दी' },
    menu: 'Sprache',
    hint: 'Diese Seite gibt es auch auf Deutsch.',
    hintLink: 'Deutsche Version',
    dismiss: 'Schließen',
  },

  chrome: {
    homeAria: 'Chess-Uno, Startseite',
    rules: 'Regeln',
    viewProfile: 'Mein Profil ansehen',
    soundOn: 'Ton einschalten',
    soundOff: 'Ton ausschalten',
    logout: 'Abmelden',
    legal: 'Impressum',
    privacy: 'Datenschutz',
    instagram: 'Chess-Uno auf Instagram',
    facebook: 'Chess-Uno auf Facebook',
  },

  feedback: {
    link: 'Feedback',
    title: 'Sag uns deine Meinung',
    lead: 'Eine Idee, ein Bug, ein Lob? Eine Nachricht genügt: Der Herausgeber des Spiels liest sie.',
    close: 'Schließen',
    kindLabel: 'Thema',
    kinds: { idea: 'Idee', bug: 'Bug', praise: 'Gefällt mir', other: 'Sonstiges' },
    messageLabel: 'Deine Nachricht',
    placeholders: {
      idea: 'Was würdest du gern in Chess-Uno finden?',
      bug: 'Was ist passiert? Auf welcher Seite, mit welchem Gerät?',
      praise: 'Was gefällt dir am meisten?',
      other: 'Schreib uns, was du uns sagen möchtest.',
    },
    contactLabel: 'Damit wir dir antworten können (optional)',
    contactPlaceholder: 'E-Mail oder Telegram-Name',
    fine: (privacy) => (
      <>
        Deine Nachricht wird vom Herausgeber gelesen, zusammen mit deinem Benutzernamen, falls du angemeldet bist ({privacy('Datenschutz')}).
      </>
    ),
    send: 'Senden',
    sending: 'Wird gesendet…',
    thanksTitle: 'Danke!',
    thanksText: 'Deine Nachricht ist angekommen. Sie hilft, das Spiel zu verbessern.',
    rateGame: 'Hat dir diese Partie gefallen?',
    starLabel: (count) => `${count} von 5`,
    rated: 'Danke für deine Bewertung!',
    tellMore: 'Sag uns, warum',
  },

  toast: {
    aria: (name) => `Herausforderung von ${name}`,
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> fordert dich heraus
      </>
    ),
    accept: 'Duell annehmen',
    later: 'Später',
  },

  auth: {
    login: 'Anmelden',
    register: 'Konto erstellen',
    username: 'Benutzername',
    password: 'Passwort',
    registerFine: (privacy) => (
      <>
        Benutzername mit 3 bis 20 Zeichen, Passwort mit mindestens 6 Zeichen. Dein Benutzername und deine Ergebnisse erscheinen in der
        öffentlichen Rangliste ({privacy('Datenschutz')}).
      </>
    ),
    submitRegister: 'Mein Konto erstellen',
    submitLogin: 'Anmelden',
  },

  landing: {
    eyebrow: 'Chess-Uno · Schach und Karten online',
    title: 'Spiel Schach mit Karten.',
    titleEm: 'Jede Runde verändert die Partie.',
    lead:
      'Bei Chess-Uno spielst du Schach mit Karten, die im Lauf der Partie aufgedeckt werden. Jede Runde verändert deine Möglichkeiten: die Anzahl der Züge, die verfügbaren Bauern oder die Armee, die du führst. Gewinne durch Schachmatt oder im Königsjagd-Modus, indem du den gegnerischen König schlägst. Fordere einen Freund heraus, finde einen Gegner für eine gewertete Partie oder übe gegen den Computer.',
    readRules: 'Regeln lesen',
    leaderboard: 'Rangliste',
    howEyebrow: 'So wird gespielt',
    howTitle: 'Eine Schachpartie mit Karten, die die Züge verändern',
    howP1:
      'Brett und Figuren funktionieren wie im Schach. Zu Beginn jeder Runde wird eine Karte gezogen und für alle aufgedeckt: Sie kann mehrere Züge gewähren, eine Runde aussetzen lassen, Bauern zurückgeben oder die Armeen tauschen. Zwei Modi: Purist, wo du durch Schachmatt gewinnst, und Königsjagd, wo du gewinnst, indem du den gegnerischen König schlägst.',
    howP2:
      'Du hast keine Karten auf der Hand. Eine Zahlenkarte gibt dir so viele Züge, wie ihr Wert angibt; du kannst diese Zugserie nach dem ersten Zug beenden. Mit den Karten +2 und +4 setzt du geschlagene Bauern wieder aufs Brett. Bei einem Richtungswechsel tauschen die Spieler ihre Armeen, ohne dass sich die Stellung der Figuren ändert. Ein Joker lässt eine Figur in dieser Runde wie eine Dame ziehen. Beide Spieler sehen immer die aufgedeckte Karte.',
    discoverRules: 'Entdecke die Regeln von Chess-Uno',
    playEyebrow: 'Online spielen',
    playTitle: 'Mit Freunden, in gewerteten Partien oder gegen den Computer',
    playP1:
      'Erstelle ein privates Duell und teile den Link, stell dich in die Warteschlange für eine gewertete Partie mit Elo oder probier die Karten gegen den Computer aus. Partien gegen den Computer ändern deine Wertung nicht.',
    playP2:
      'Mit einem Konto kannst du einen Freund über einen privaten Link einladen oder automatisch nach einem Gegner suchen. Beendete Partien zwischen zwei Konten verändern deine Elo-Zahl; die Rangliste zeigt außerdem die neuesten Ergebnisse und deine Direktvergleiche. Um die Effekte ganz ohne Risiko kennenzulernen, starte eine Trainingspartie gegen den Computer. Vor einem privaten Duell kannst du festlegen, welche Karten im Stapel liegen und wie hoch die höchste Zahlenkarte ist.',
    heroAlt: 'Ein Schachbauer vor Spielkarten',
    faqEyebrow: 'Häufige Fragen',
    faqTitle: 'Alles über Chess-Uno',
    faq: [
      {
        q: 'Was ist Chess-Uno?',
        a: 'Chess-Uno ist eine Schachvariante, die du kostenlos online im Browser spielen kannst. Gespielt wird auf einem normalen Schachbrett mit den üblichen Figuren, aber zu Beginn jeder Runde wird eine Karte gezogen und beiden Spielern gezeigt: Sie legt fest, was der Spieler in dieser Runde tun kann.',
      },
      {
        q: 'Wie gewinnt man eine Partie?',
        a: 'Das hängt vom Modus ab, der vor der Partie gewählt wird. Im Purist-Modus darfst du deinen König wie im Schach nie im Schach lassen, und du gewinnst durch Schachmatt: wenn der Gegner das Schach mit der gerade gezogenen Karte nicht abwehren kann. Im Königsjagd-Modus darfst du deinen König angegriffen lassen, und du gewinnst, indem du den gegnerischen König schlägst.',
      },
      {
        q: 'Was bewirken die Karten?',
        a: 'Eine Zahlenkarte gibt so viele Züge, wie ihr Wert angibt (standardmäßig 1 bis 5). Bei Aussetzen muss der Spieler eine Runde aussetzen. Die Karten +2 und +4 geben geschlagene Bauern zurück, die wieder aufs Brett gesetzt werden. Der Richtungswechsel tauscht die Armeen der beiden Spieler. Der Joker lässt eine Figur wie eine Dame ziehen. Niemand behält Karten auf der Hand.',
      },
      {
        q: 'Kann ich allein spielen?',
        a: 'Ja, gegen den Computer, auf der Stufe Leicht oder Mittel. Diese Trainingspartien zählen nicht für die Rangliste. Du kannst auch einen Freund über einen privaten Link herausfordern oder in der Warteschlange für gewertete Partien einen Gegner finden.',
      },
      {
        q: 'Wie funktioniert die Rangliste?',
        a: 'Alle Spieler starten mit 1000 Elo. Gewertet werden nur beendete Partien zwischen zwei Konten. Die Stufen reichen von Bronze bis Diamant (ab 1400 Elo), und die Direktvergleiche zwischen Spielern sind öffentlich.',
      },
      {
        q: 'Muss ich etwas bezahlen oder installieren?',
        a: 'Nein. Chess-Uno ist kostenlos und läuft im Browser, auf dem Computer wie auf dem Handy. Du brauchst nur ein Konto mit Benutzername und Passwort, ganz ohne E-Mail-Adresse.',
      },
      {
        q: 'Hat Chess-Uno etwas mit dem Spiel UNO zu tun?',
        a: 'Nein. Chess-Uno ist ein unabhängiges Spiel und weder mit Mattel, der Inhaberin der Marke UNO, verbunden noch von Mattel unterstützt oder gesponsert.',
      },
    ],
  },

  home: {
    greeting: (hour) => (hour >= 5 && hour < 11 ? 'Guten Morgen' : hour >= 11 && hour < 18 ? 'Guten Tag' : 'Guten Abend'),
    play: 'Spielen',
    ranked: 'Gewertete Partie',
    rankedDetail: 'Gegner wird automatisch gefunden',
    challengeFriend: 'Freund herausfordern',
    challengeFriendDetail: 'Privater Link zum Teilen',
    join: 'Beitreten',
    joinDetail: 'Mit einem Einladungscode',
    training: 'Training',
    trainingDetail: 'Gegen die KI, ungewertet',
    tournament: 'Turnier',
    tournamentDetail: 'K.-o.-System, mit einem Code',
    tournamentOngoing: (name) => `Laufendes Turnier: ${name}`,
    tournamentOpen: (name) => `Anmeldung offen: ${name}`,
    rules: 'Spielregeln',
    waitingGame: 'Partie wartet auf einen Gegner',
    ongoingAgainst: (name) => `Laufende Partie gegen ${name}`,
    resume: 'Fortsetzen',
    leaderboard: 'Gesamtrangliste',
    recentResults: 'Neueste Ergebnisse',
    headToHead: 'Direktvergleich',
    rivalries: 'Große Rivalitäten',
  },

  join: {
    eyebrow: 'Einladung',
    title: 'Einer Partie beitreten',
    lead: 'Gib den Code ein, den dir dein Gegner geschickt hat.',
    code: 'Einladungscode',
    submit: 'Partie beitreten',
  },

  invite: {
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> fordert dich heraus
      </>
    ),
    invited: 'Du bist zu einem Duell eingeladen',
  },

  gate: {
    title: 'Anmelden',
    lead: 'Melde dich an, um zu spielen und in der Rangliste aufzusteigen.',
  },

  queue: {
    searching: 'Suche nach einem Gegner…',
    chooseTitle: 'Wähle deinen Modus',
    chooseLead: 'Du triffst auf einen Spieler, der denselben Modus gewählt hat. Die Elo-Wertung gilt für beide Modi gemeinsam.',
    changeMode: 'Modus wechseln',
    eyebrow: 'Gewertete Partie',
    lead: 'Die Partie beginnt, sobald ein weiterer Spieler der Warteschlange beitritt.',
  },

  competition: {
    justNow: 'gerade eben',
    minutesAgo: (m) => `vor ${m} Min.`,
    hoursAgo: (h) => `vor ${h} Std.`,
    daysAgo: (d) => `vor ${n(d, 'Tag', 'Tagen')}`,
    tierTitle: (label) => `Stufe ${label}`,
    streakTitle: (count) => `${n(count, 'Sieg', 'Siege')} in Folge`,
    form: { W: 'Sieg', L: 'Niederlage', D: 'Remis' },
    formAria: (items) => `Form: ${items.join(', ')}`,
    yourRanking: 'Deine Platzierung',
    outOf: (count) => `von ${n(count, 'gewerteten Spieler', 'gewerteten Spielern')}`,
    unranked: 'Nicht gewertet — spiel eine gewertete Partie',
    pointsFor: (missing, label) => `Noch ${missing} Punkte bis ${label}`,
    maxTier: 'Höchste Stufe',
    pointsBefore: (missing, label) => (
      <>
        <b>{missing}</b> Punkte bis {label}
      </>
    ),
    maxTierCaption: 'Höchste Stufe — verteidige deinen Platz',
    wins: 'Siege',
    losses: 'Niederlagen',
    draws: 'Remis',
    winRate: 'Siegquote',
    percent: (value) => `${value} %`,
    streak: 'Serie',
    best: (count) => `Rekord ${count}`,
    leaderboardEmpty: 'Noch ist niemand gewertet. Gewinne eine Partie gegen einen Freund, um die Rangliste zu eröffnen.',
    you: 'du',
    record: (w, l, d) => `${w} S · ${l} N${d ? ` · ${d} R` : ''}`,
    challenge: 'Herausfordern',
    taunts: {
      never: 'Noch nie gegeneinander gespielt: Hol dir den ersten Punkt.',
      twoLosses: 'Zwei Niederlagen in Folge. Zeit für eine Revanche.',
      dominate: 'Du dominierst dieses Duell.',
      lead: 'Du führst, aber noch ist nichts entschieden.',
      tied: 'Perfekter Gleichstand.',
      escape: 'Dieses Duell entgleitet dir.',
      behind: 'Du liegst zurück.',
    },
    rivalsEmpty: 'Noch kein Direktvergleich. Fordere einen Spieler aus der Rangliste heraus.',
    revenge: 'Revanche',
    replay: 'Nochmal spielen',
    drawsShort: (d) => `${d} R`,
    rivalriesEmpty: 'Eine Rivalität entsteht, sobald zwei Spieler zweimal gegeneinander gespielt haben.',
    rivalryMeta: (games, draws) => `${n(games, 'Partie', 'Partien')}${draws ? ` · ${n(draws, 'Remis', 'Remis')}` : ''}`,
    recentEmpty: 'Noch keine gewerteten Partien.',
    and: 'und',
    drew: 'spielen Remis',
    beat: 'schlägt',
    reasons: {
      king_captured: 'König geschlagen',
      checkmate: 'Schachmatt',
      resign: 'Aufgabe',
      abandon: 'Verbindungsabbruch',
      draw_agreed: 'Remis vereinbart',
      no_move_limit: 'Remis durch Inaktivität',
    },
  },

  settings: {
    decrease: 'Verringern',
    increase: 'Erhöhen',
    maxNumberAria: 'Höchste Zahlenkarte',
    keepCards: (max) => `Karten 1 bis ${max} behalten`,
    eyebrowBot: 'Training · ungewertet',
    eyebrowPrivate: 'Private Partie · gewertet',
    title: 'Partie-Einstellungen',
    leadBot: 'Stell den Gegner und den Kartenstapel ein und starte dann die Partie.',
    leadPrivate: 'Stell den Kartenstapel ein; für deinen Gegner wird ein Einladungslink erstellt.',
    opponent: 'Gegner',
    opponentHint: 'Experimentelle Stufen: Die KI bekommt mehr oder weniger Bedenkzeit.',
    easy: 'Leicht',
    easyDesc: 'Schaut einen gegnerischen Zug voraus.',
    medium: 'Mittel',
    mediumDesc: 'Schaut zwei Züge voraus und denkt länger nach.',
    side: 'Deine Seite',
    sideHint: 'Ein Richtungswechsel kann die Armeen während der Partie tauschen.',
    whiteDesc: 'Du beginnst',
    blackDesc: 'Die KI beginnt',
    random: 'Zufällig',
    randomSummary: 'Auslosung',
    rules: 'Regeln',
    rulesHint: 'Zwei Spielweisen: wie im Schach oder als Jagd auf den König.',
    deck: 'Kartenstapel',
    deckHint: 'Eine Zahlenkarte gibt so viele Züge, wie ihr Wert angibt.',
    numberCards: 'Zahlenkarten',
    fromOneTo: (max) => (
      <>
        von 1 bis <b>{max}</b>
      </>
    ),
    jokers: 'Joker',
    jokersDesc: 'Ein Zug, bei dem eine beliebige Figur wie eine Dame zieht.',
    advanced: 'Erweiterte Regeln',
    advancedHint: 'Bauern zurückholen, Zugserien-Schutz, Remis.',
    plus2: 'Von einer +2 zurückgegebene Bauern',
    plus4: 'Von einer +4 zurückgegebene Bauern',
    drawAfter: 'Remis nach N Runden ohne Zug',
    safeguard: 'Zugserien-Schutz',
    safeguardDesc: 'Eine Zugserie endet, sobald der gegnerische König angegriffen wird.',
    summary: 'Übersicht',
    mode: 'Modus',
    modeBot: 'Gegen die KI',
    modePrivate: 'Privater Link',
    level: 'Stufe',
    camp: 'Seite',
    deckSize: (count) => n(count, 'Karte', 'Karten'),
    numbers: 'Zahlen',
    range: (max) => `1 bis ${max}`,
    jokersIncluded: 'Joker',
    ranking: 'Wertung',
    unranked: 'Ungewertet',
    eloAtStake: 'Elo-Punkte im Spiel',
    start: 'Partie starten',
    create: 'Partie erstellen',
    locked: 'Die Regeln stehen fest, sobald die Partie beginnt.',
  },

  game: {
    drawAgreed: 'Remis (in gegenseitigem Einvernehmen).',
    drawIdle: 'Remis (Runden ohne Zug).',
    result: (mine, winner, reason) => {
      const why: Partial<Record<GameEndReason, string>> = {
        king_captured: 'Schlagen des Königs',
        checkmate: 'Schachmatt',
        resign: 'Aufgabe',
        abandon: 'längeren Verbindungsabbruch',
      }
      return `${mine ? 'Sieg' : 'Niederlage'} — ${winner} gewinnt durch ${why[reason] ?? reason}.`
    },
    headToHead: 'Direktvergleich',
    firstDuel: 'Erstes Duell',
    youLead: 'Du führst',
    leads: (name) => `${name} führt`,
    tied: 'Gleichstand',
    training: (level) => `Trainingspartie gegen die KI (Stufe ${level}): ungewertet.`,
    computing: 'Wertung wird berechnet…',
    promotion: (tier) => `Aufstieg: Stufe ${tier}`,
    demotion: (tier) => `Abstieg auf ${tier}… Zeit für eine Revanche.`,
    recordAgainst: (name) => `Bilanz gegen ${name}:`,
    streak: (count) => `${n(count, 'Sieg', 'Siege')} in Folge`,
    backHome: 'Zurück zur Startseite',
    privateGame: 'Private Partie',
    waiting: 'Warte auf einen Gegner…',
    shareLink: 'Teile diesen Link:',
    copied: 'Kopiert!',
    copyLink: 'Link kopieren',
    inviteText: (name) => `${name} fordert dich bei Chess-Uno heraus! Nimm das Duell an:`,
    messageCopied: 'Nachricht kopiert!',
    sent: 'Gesendet!',
    sendChallenge: 'Herausforderung senden',
    code: 'Code:',
    notified: 'Der herausgeforderte Spieler bekommt außerdem eine Benachrichtigung, wenn er online ist.',
    loadingGame: 'Partie wird geladen…',
    connecting: '(Verbindung zum Server…)',
    connected: 'Verbunden',
    reconnecting: 'Verbindung wird wiederhergestellt…',
    acceptDraw: 'Remis annehmen',
    decline: 'Ablehnen',
    offerDraw: 'Remis anbieten',
    offerDrawShort: 'Remis',
    offerDrawConfirm: 'Deinem Gegner Remis anbieten?',
    acceptDrawShort: 'Annehmen',
    resignConfirm: 'Partie aufgeben?',
    resign: 'Aufgeben',
    resultAria: 'Ergebnis',
    rematchOffered: (name) => `${name} bietet eine Revanche an.`,
    rematchWaiting: 'Revanche angeboten: Warte auf den Gegner…',
    rematch: 'Revanche',
    closeResult: 'Schließen, um das Brett zu sehen',
    showResult: 'Ergebnis anzeigen',
    verdicts: { win: 'Sieg', loss: 'Niederlage', draw: 'Remis' },
    winText: (opponent, gain, rating) => `Ich habe ${opponent} bei Chess-Uno geschlagen (+${gain} Elo, ${rating}). Wer ist der Nächste?`,
    shared: 'Geteilt!',
    shareWin: 'Meinen Sieg teilen',
  },

  panels: {
    phases: {
      turn_start: 'Rundenbeginn',
      draw: 'Karte ziehen',
      effect_choice: 'Effekt wählen (Joker)',
      place_pawns: 'Zurückgeholte Bauern einsetzen',
      moves: 'Züge',
      turn_end: 'Rundenende',
      game_over: 'Partie beendet',
    },
    playsArmy: (army) => `Spielt ${army === 'w' ? 'Weiß' : 'Schwarz'}`,
    chooseMoves: (you) => (you ? 'Wähle deine Anzahl an Zügen' : 'wählt die Anzahl der Züge'),
    placePawns: (you, count) =>
      you ? `Setze ${n(count, 'Bauern', 'Bauern')} ein (grüne Felder)` : `setzt ${n(count, 'Bauern', 'Bauern')} ein`,
    joker: (you) => (you ? 'Joker: Zieh eine beliebige Figur wie eine Dame' : 'Joker: zieht eine Figur wie eine Dame'),
    moreMoves: (you, count) => (you ? `Noch ${n(count, 'Zug', 'Züge')} (oder beende die Zugserie)` : `noch ${n(count, 'Zug', 'Züge')}`),
    playMoves: (you, count) => (you ? `Spiele ${n(count, 'Zug', 'Züge')}` : `spielt ${n(count, 'Zug', 'Züge')}`),
    pipsAria: (made, total) => `${made} von ${n(total, 'Zug', 'Zügen')} gespielt`,
    yourTurn: 'Du bist dran',
    turnOf: (name) => `${name} ist dran`,
    check: 'Schach!',
    go: 'Los!',
    gameStarts: 'Die Partie beginnt',
    drawing: (you, summary) => `${you ? 'Du ziehst' : 'zieht'}: ${summary}`,
    inCheckYou: (mode) =>
      mode === 'purist' ? 'Dein König steht im Schach: Wehr es mit deiner Karte ab.' : 'Dein König wird angegriffen: Wehr den Angriff mit deiner Karte ab.',
    inCheckOpponent: (mode) =>
      mode === 'purist'
        ? 'Gegnerischer König im Schach: Ohne Abwehr ist es Schachmatt.'
        : 'Gegnerischer König angegriffen: Ohne Abwehr wird er geschlagen.',
    turnNumber: (turn) => `Runde ${turn}`,
    discardTitle: (owner, count) => `Ablagestapel von ${owner}: ${n(count, 'Karte', 'Karten')}`,
    discard: 'Ablagestapel',
    seatAria: (name) => `Spieler ${name}`,
    connected: 'Verbunden',
    disconnected: 'Getrennt',
    you: '(du)',
    ai: 'KI',
    eloTitle: (tier) => `Elo-Wertung · ${tier}`,
    seatYourTurn: 'du bist dran',
    seatPlaying: 'spielt',
    pawnsTitle: 'Geschlagene Bauern, die eine +2 oder +4 zurückholen kann',
    toRecover: (count) => `${count} zurückzuholen`,
    turnCardAria: 'Karte der Runde',
    drawPile: 'Nachziehstapel',
    youDrew: 'Du hast gezogen',
    drew: (name) => `${name} hat gezogen`,
    revealed: (label) => `Aufgedeckt: ${label}`,
    noCard: 'Keine Karte',
    jokerChoiceAria: 'Wahl der Anzahl an Zügen',
    jokerChoice: 'Joker: Wie viele Züge in dieser Runde?',
    placementHint: (count) => `Klicke auf ein grünes Feld des Bretts, um einen Bauern einzusetzen (noch ${count}).`,
    endSeries: (count) => `Zugserie beenden (${n(count, 'Zug', 'Züge')} nicht gespielt)`,
    effects: {
      number: (count) => `${n(count, 'Zug', 'Züge')} zu spielen.`,
      zero: 'Runde ohne Zug ausgesetzt.',
      skip: 'Aussetzen: Der Gegner zieht sofort eine Karte und spielt sie aus.',
      reverse: 'Richtungswechsel: Die Spieler tauschen ihre Armeen; der aktuelle Spieler macht mit der neuen weiter.',
      plus: (card, pawns, moves) => `${card}: holt bis zu ${pawns} geschlagene Bauern zurück, dann ${n(moves, 'Zug', 'Züge')}.`,
      wild: 'Joker: ein Zug, bei dem eine beliebige Figur wie eine Dame zieht.',
    },
    reveal: {
      who: (you, name) => (you ? 'Du ziehst' : `${name} zieht`),
      moves: (count) => n(count, 'Zug', 'Züge'),
      zero: 'Runde ausgesetzt',
      zeroDetail: 'Kein Zug in dieser Runde.',
      skip: 'Muss aussetzen',
      skipDetail: (name) => `${name} zieht eine Karte und spielt.`,
      reverse: 'Richtungswechsel!',
      reverseDetail: 'Die Spieler tauschen ihre Armeen.',
      recovers: (count) => `Holt ${n(count, 'Bauern', 'Bauern')} zurück`,
      then: (count) => `Dann ${n(count, 'Zug', 'Züge')}.`,
      oneMove: '1 Zug',
      noPawn: 'Kein Bauer zum Zurückholen.',
      joker: 'Joker',
      jokerDetail: 'Eine Figur deiner Wahl zieht wie eine Dame.',
    },
    yourKingAttacked: 'Dein König wird angegriffen',
    enemyKingAttacked: 'Der gegnerische König wird angegriffen',
    youWin: 'Du gewinnst',
    wins: (name) => `${name} gewinnt`,
    pieces: { p: 'der Bauer', n: 'der Springer', b: 'der Läufer', r: 'der Turm', q: 'die Dame', k: 'der König' },
    somePiece: 'die Figur',
    deliversMate: (piece, from) => `${piece} auf ${from} setzt schachmatt`,
    takesKing: (piece, from, to) => `${piece} auf ${from} schlägt den König auf ${to}`,
    kingTakenOn: (square) => `Der König wird auf ${square} geschlagen`,
    checkmate: 'Schachmatt',
    kingCaptured: 'König geschlagen',
    statusAria: 'Spielstand',
    phase: 'Phase',
    actor: 'Spieler an der Reihe',
    movesRemaining: 'Verbleibende Züge',
    movesValue: (remaining, made) => `${remaining} (gespielt: ${made})`,
    idleTurns: 'Runden ohne Zug',
    carefulKing: 'Vorsichtiger König: Der König darf nicht auf ein angegriffenes Feld ziehen.',
    kingAttacked: (army, mode) =>
      mode === 'purist'
        ? `Der ${army === 'w' ? 'weiße' : 'schwarze'} König steht im Schach.`
        : `Der ${army === 'w' ? 'weiße' : 'schwarze'} König wird angegriffen (nur zur Information, keine Reaktion nötig).`,
    youOfferedDraw: 'Du hast Remis angeboten.',
    offersDraw: (name) => `${name} bietet Remis an.`,
    log: 'Protokoll',
  },

  board: {
    promotionAria: 'Umwandlung wählen',
    promotion: 'Umwandlung:',
    pieces: { q: 'Dame', r: 'Turm', b: 'Läufer', n: 'Springer' },
  },

  card: {
    back: 'Kartenrückseite',
  },

  tournament: {
    eyebrow: 'Turnier',
    hubTitle: 'K.-o.-Turnier',
    hubLead: 'Jede Niederlage bedeutet das Aus: Wer als Letzter übrig bleibt, gewinnt das Turnier.',
    joinTitle: 'Einem Turnier beitreten',
    joinCode: 'Turniercode',
    joinSubmit: 'Beitreten',
    createTitle: 'Turnier veranstalten',
    createHint: 'Du bekommst einen Code zum Teilen. Du startest das Turnier, sobald alle da sind.',
    name: 'Name (optional)',
    namePlaceholder: 'Freitagabend-Pokal',
    modeLabel: 'Spielmodus',
    sizeLabel: 'Maximale Spielerzahl',
    create: 'Turnier erstellen',
    mine: 'Deine Turniere',
    back: 'Turniere',
    defaultName: (host) => `Turnier von ${host}`,
    status: { registration: 'Anmeldung offen', running: 'Läuft', finished: 'Beendet', cancelled: 'Abgesagt' },
    playerCount: (count, max) => `${count} / ${max} Spieler`,
    hostedBy: (name) => `Veranstaltet von ${name}`,
    open: 'Öffnen',
    codeLabel: 'Code',
    copyLink: 'Link kopieren',
    copied: 'Kopiert!',
    share: 'Teilen',
    shared: 'Geteilt!',
    shareText: (name, host) => `${host} lädt dich zum Turnier „${name}“ bei Chess-Uno ein. Mach mit:`,
    players: 'Spieler',
    host: 'Veranstalter',
    you: 'du',
    join: 'Turnier beitreten',
    leave: 'Turnier verlassen',
    start: 'Turnier starten',
    startHint: 'Es braucht mindestens 2 Spieler. Der Turnierbaum wird beim Start ausgelost; danach kann sich niemand mehr anmelden.',
    waitingHost: 'Warte darauf, dass der Veranstalter das Turnier startet…',
    cancel: 'Turnier absagen',
    cancelConfirm: 'Dieses Turnier für alle Spieler absagen?',
    yourMatch: (opponent) => `Dein Match gegen ${opponent} ist bereit`,
    play: 'Spielen',
    waitingNext: 'Nächstes Match: Warte auf die anderen Ergebnisse…',
    byeThrough: 'Du kommst kampflos in die nächste Runde.',
    eliminated: (round) => `${round.startsWith('Runde') ? 'In' : 'Im'} ${round} ausgeschieden. Verfolge den Rest hier.`,
    champion: 'Turniersieger',
    youWon: 'Glückwunsch, du gewinnst das Turnier!',
    cancelled: 'Dieses Turnier wurde vom Veranstalter abgesagt.',
    bracket: 'Turnierbaum',
    roundName: (fromEnd) =>
      ({ 1: 'Finale', 2: 'Halbfinale', 3: 'Viertelfinale', 4: 'Achtelfinale', 5: 'Sechzehntelfinale' })[fromEnd] ?? `Runde ${fromEnd}`,
    roundOne: (fromEnd) =>
      ({ 1: 'Finale', 2: 'Halbfinale', 3: 'Viertelfinale', 4: 'Achtelfinale', 5: 'Sechzehntelfinale' })[fromEnd] ?? `Runde ${fromEnd}`,
    tbd: 'Steht noch nicht fest',
    bye: 'Kampflos weiter',
    matchStates: { waiting: 'Wartet', playing: 'Läuft', done: 'Beendet' },
    endedBy: {
      king_captured: 'König geschlagen',
      checkmate: 'Schachmatt',
      resign: 'Aufgabe',
      abandon: 'Spielverlust',
      draw_agreed: 'Remis vereinbart',
      no_move_limit: 'Remis durch Inaktivität',
      coin_toss: 'Münzwurf',
    },
    redraws: (count) => `${n(count, 'Remis', 'Remis')} wiederholt`,
    rulesTitle: 'Turnierregeln',
    rules: (idleMinutes) => [
      'K.-o.-System: Der Verlierer eines Matches scheidet aus, der Gewinner zieht in die nächste Runde ein.',
      'Der Turnierbaum wird beim Start des Turniers ausgelost. Ist die Spielerzahl keine Zweierpotenz, kommen einige Spieler kampflos durch die erste Runde.',
      'Eine Remispartie wird mit getauschten Farben wiederholt; nach drei Remis entscheidet ein Münzwurf.',
      `Keine Revanche. Wer ${n(idleMinutes, 'Minute', 'Minuten')} lang nicht spielt oder getrennt bleibt, verliert die Partie.`,
      'Turnierpartien zählen für die Elo-Rangliste.',
    ],
    notFoundTitle: 'Turnier nicht gefunden',
    notFoundLead: 'Überprüfe den Code, den du vom Veranstalter bekommen hast.',
    invited: 'Du bist zu einem Turnier eingeladen',
    invitedTo: (name) => (
      <>
        Du bist zum Turnier <strong>{name}</strong> eingeladen
      </>
    ),
    toast: {
      aria: (name) => `Turniermatch: ${name}`,
      ready: (opponent) => (
        <>
          Dein Match gegen <strong>{opponent}</strong> ist bereit
        </>
      ),
      play: 'Spielen',
      later: 'Später',
    },
    game: {
      strip: (name, round) => `${name} · ${round}`,
      rule: (idleMinutes) => `Keine Revanche · höchstens ${n(idleMinutes, 'Minute', 'Minuten')} Inaktivität`,
      bracket: 'Turnierbaum ansehen',
      backToBracket: 'Zurück zum Turnierbaum',
      drawReplay: 'Remis: Das Match wird mit getauschten Farben wiederholt.',
    },
  },

  profile: {
    notFound: 'Spieler nicht gefunden.',
    eyebrow: 'Profil',
    challenge: (name) => `${name} herausfordern`,
    shareMe: (rank, rating) =>
      `Ich habe ${rating} Elo bei Chess-Uno${rank ? ` und stehe auf Platz ${rank}` : ''}. Komm und stoß mich vom Thron!`,
    shareOther: (name, rating) => `${name} hat ${rating} Elo bei Chess-Uno. Wer nimmt die Herausforderung an?`,
    linkCopied: 'Link kopiert!',
    shared: 'Geteilt!',
    share: 'Teilen',
    yourRanking: 'Deine Platzierung',
    ranking: 'Rangliste',
    youVs: (name) => `Du gegen ${name}`,
    draws: (count) => n(count, 'Remis', 'Remis'),
    headToHead: 'Direktvergleich',
    recentResults: 'Neueste Ergebnisse',
  },

  rules: {
    eyebrow: 'Regelwerk',
    toc: 'Inhalt',
  },

  legal: {
    updated: (date) => `Zuletzt aktualisiert: ${date}`,
  },
}
